import { t } from 'i18next';
import { CirclePlusIcon } from 'lucide-react';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import type { I18nPrefix } from '../../../i18n/prefix';
import type { DashboardPageProps } from '../../../lib/dashboard-page';
import { AuthLayout } from '../../auth/components/auth-layout';
import { AuthLogo } from '../../auth/components/auth-logo';
import { CreateTeamForm } from '../components/create-team-form';

const PAGE = 'features:no_team.pages.no_team_page' satisfies I18nPrefix;

export function NoTeamPage({ handlers }: DashboardPageProps) {
  const { navigationHandler } = handlers;
  const [showCreateTeamForm, setShowCreateTeamForm] = useState<boolean>(false);

  function navigateToLogout() {
    navigationHandler.navigate('/logout', { excludeSiteId: true, excludeSiteEnv: true });
  }

  /** Hand the new team to `/switch-account`, which activates it and routes on to platform setup. */
  function changeSite(teamId: string) {
    // add `new-team` directly to the URL as a valueless flag
    navigationHandler.navigate(`/switch-account/${teamId}?new-team`, {
      excludeSiteId: true,
      excludeSiteEnv: true,
    });
  }

  return (
    <AuthLayout>
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <AuthLogo />
        <div className="mt-8 rounded-lg bg-spark-surface p-10 shadow-xl sm:p-14">
          <h2 className="mb-4 font-bold text-spark-text-primary text-xl sm:text-2xl">
            {t(`${PAGE}.heading`)}
          </h2>

          <p className="max-w mt-2 text-sm text-spark-text-secondary">
            <Trans
              ns="features"
              i18nKey="no_team.pages.no_team_page.description"
              components={{
                support: (
                  <a
                    href="https://docs.sparklayer.io/support"
                    className="font-medium text-spark-accent hover:text-spark-accent-hover"
                  >
                    {t(`${PAGE}.support_link`)}
                  </a>
                ),
              }}
            />
          </p>

          <div className="mt-4 flex items-center gap-4">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md border border-spark-border-default bg-spark-surface px-4 py-2 font-medium text-sm text-spark-text-primary shadow-xs hover:bg-spark-surface-subtle focus:outline-hidden focus:ring-2 focus:ring-spark-accent-hover focus:ring-offset-2"
              onClick={navigateToLogout}
            >
              {t(`${PAGE}.sign_out`)}
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md border border-spark-border-subtle bg-indigo-600 px-4 py-2 font-medium text-sm text-white shadow-xs hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-spark-accent-hover focus:ring-offset-2"
              onClick={() => setShowCreateTeamForm(true)}
            >
              <CirclePlusIcon className="mr-2 size-4" />
              {t(`${PAGE}.create_team`)}
            </button>
          </div>
        </div>

        <CreateTeamForm
          handlers={handlers}
          isOpen={showCreateTeamForm}
          onClose={() => setShowCreateTeamForm(false)}
          onTeamCreated={(teamId) => {
            setShowCreateTeamForm(false);
            changeSite(teamId);
          }}
        />
      </div>
    </AuthLayout>
  );
}
